import React, { useState } from 'react';
import { View, KeyboardAvoidingView, Platform, Text, ScrollView } from 'react-native';
import { Button, HelperText } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { changePassword, logout } from '../redux/slices/authSlice';
import { RADIUS, TYPOGRAPHY, shadow } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { TextInput, ThemeToggle } from '../components/ui';

export default function ChangePasswordScreen() {
  const dispatch = useAppDispatch();
  const { loading, error, username } = useAppSelector((s) => s.auth);
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, gutter } = useLayout();
  const insets = useSafeAreaInsets();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (newPassword.length < 8) {
      setLocalError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    if (newPassword === currentPassword) {
      setLocalError('Le nouveau mot de passe doit être différent du mot de passe provisoire.');
      return;
    }
    setLocalError(null);
    dispatch(changePassword({ currentPassword, newPassword }));
  };

  const message = localError || error;

  return (
    <KeyboardAvoidingView style={styles.root} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        style={styles.root}
        contentContainerStyle={[styles.scroll, { paddingHorizontal: gutter, paddingTop: insets.top + 72 }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.card, isDesktop ? [styles.cardDesktop, shadow(colors, 2)] : null]}>
          <View style={styles.iconBadge}>
            <MaterialCommunityIcons name="shield-lock-outline" size={28} color={colors.primary} />
          </View>
          <Text style={styles.title} accessibilityRole="header">Nouveau mot de passe</Text>
          <Text style={styles.subtitle}>
            {username ? `Bonjour ${username}. ` : ''}
            Choisissez un mot de passe personnel pour continuer. Cette étape est obligatoire.
          </Text>

          <TextInput
            label="Mot de passe provisoire"
            value={currentPassword}
            onChangeText={setCurrentPassword}
            secureTextEntry={!showPassword}
            style={styles.input}
            left={<TextInput.Icon icon="lock-outline" />}
            right={
              <TextInput.Icon
                icon={showPassword ? 'eye-off-outline' : 'eye-outline'}
                onPress={() => setShowPassword((v) => !v)}
                accessibilityLabel={showPassword ? 'Masquer les mots de passe' : 'Afficher les mots de passe'}
              />
            }
          />

          <TextInput
            label="Nouveau mot de passe"
            value={newPassword}
            onChangeText={setNewPassword}
            secureTextEntry={!showPassword}
            style={styles.inputWithHint}
            left={<TextInput.Icon icon="lock-reset" />}
          />
          <HelperText type="info" visible style={styles.hint}>
            8 caractères minimum, différent du mot de passe provisoire.
          </HelperText>

          <TextInput
            label="Confirmer le mot de passe"
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry={!showPassword}
            style={styles.input}
            left={<TextInput.Icon icon="lock-check-outline" />}
          />

          {message ? (
            <View style={styles.errorBox} accessibilityRole="alert">
              <MaterialCommunityIcons name="alert-circle-outline" size={18} color={colors.danger} />
              <Text style={styles.errorText}>{message}</Text>
            </View>
          ) : null}

          <Button
            mode="contained"
            onPress={handleSubmit}
            loading={loading}
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            style={styles.button}
            contentStyle={styles.buttonContent}
            labelStyle={styles.buttonLabel}
          >
            Enregistrer
          </Button>

          <View style={styles.footer}>
            <Button
              mode="text"
              icon="logout"
              textColor={colors.textPrimary}
              onPress={() => dispatch(logout())}
              labelStyle={styles.logoutLabel}
            >
              Se déconnecter
            </Button>
          </View>
        </View>
      </ScrollView>

      <ThemeToggle style={[styles.themeToggle, { top: insets.top + 16 }]} />
    </KeyboardAvoidingView>
  );
}

const useStyles = makeStyles((c) => ({
  root: { flex: 1, backgroundColor: c.background },
  scroll: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 72,
    paddingBottom: 40,
  },
  card: {
    width: '100%',
    maxWidth: 440,
  },
  cardDesktop: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: c.divider,
    padding: 32,
  },
  iconBadge: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: c.primarySoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: { ...TYPOGRAPHY.title, fontSize: 26, lineHeight: 32, color: c.textPrimary, marginBottom: 8 },
  subtitle: { ...TYPOGRAPHY.body, color: c.textSecondary, marginBottom: 24 },
  input: { marginBottom: 14 },
  inputWithHint: { marginBottom: 0 },
  hint: { paddingHorizontal: 4, marginBottom: 6 },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: c.dangerSoft,
    borderRadius: RADIUS.md,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 4,
  },
  errorText: { flex: 1, fontSize: 14, lineHeight: 20, color: c.danger, fontWeight: '500' },
  button: { marginTop: 12, borderRadius: RADIUS.sm },
  buttonContent: { height: 48 },
  buttonLabel: { fontSize: 16, fontWeight: '600' },
  footer: { alignItems: 'center', marginTop: 16 },
  logoutLabel: { textDecorationLine: 'underline' },
  themeToggle: { position: 'absolute', top: 16, right: 16 },
}));
